<template>
  <div id="app">
    <main-layout 
      ref="mainLayout"
      :current-menu="currentMenu"
      @menu-change="handleMenuChange" 
      @logout="handleLogout"
      @view-profile="showUserProfile"
      @show-login="showLoginPrompt"
    >
      <component 
        :is="currentPage" 
        @navigate="handleNavigate"
        @play-video="handlePlayVideo"
        @play-audio="handlePlayAudio"
        @show-login="showLoginPrompt"
        :video-id="videoId"
        :audio-id="audioId"
        :category-id="currentCategoryId"
        :page-type="currentMenu"
        :is-logged-in="isLoggedIn"
        @close-video="closeVideo"
        @close-audio="closeAudio"
        @update:videoId="updateVideoId"
        @update:audioId="updateAudioId"
      ></component>
    </main-layout>

    <!-- 登录弹窗 -->
    <div v-if="showLoginModal" class="login-modal-overlay" @click.self="cancelLogin">
      <div class="login-modal">
        <div class="login-modal-header">
          <h3>登录</h3>
          <button class="close-button" @click="cancelLogin">&times;</button>
        </div>
        <div class="login-modal-body">
          <login-form @login-success="handleLoginSuccess"></login-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainLayout from './components/MainLayout.vue';
import LoginForm from './components/LoginForm.vue';
import Home from './views/Home.vue';
import ContentPage from './views/ContentPage.vue';
import UserProfile from './views/UserProfile.vue';
import VideoPlayer from './views/VideoPlayer.vue';
import AudioPlayer from './views/AudioPlayer.vue';
import ParentalControl from './views/ParentalControl.vue';
import DisputeResolution from './views/DisputeResolution.vue';
import MinorProtection from './views/MinorProtection.vue';
import AboutUs from './views/AboutUs.vue';
import ContactUs from './views/ContactUs.vue';
import ReportCenter from './views/ReportCenter.vue';

export default {
  name: 'App',
  components: {
    MainLayout,
    LoginForm,
    Home,
    ContentPage,
    UserProfile,
    VideoPlayer,
    AudioPlayer,
    ParentalControl,
    DisputeResolution,
    MinorProtection,
    AboutUs,
    ContactUs,
    ReportCenter
  },
  data() {
    return {
      isLoggedIn: false,
      currentMenu: 'home',
      videoId: null,
      audioId: null,
      currentCategoryId: null,
      showLoginModal: false,
      pendingVideoId: null,
      pendingAudioId: null
    }
  },
  computed: {
    currentPage() {
      // 如果有视频ID，优先显示视频播放页面
      if (this.videoId) {
        return 'VideoPlayer';
      }
      
      // 如果有音频ID，显示音频播放页面
      if (this.audioId) {
        return 'AudioPlayer';
      }
      
      switch (this.currentMenu) {
        case 'mv':
          return 'ContentPage';
        case 'singer':
          return 'ContentPage';
        case 'all':
          return 'ContentPage';
        case 'profile':
          // 检查是否登录，未登录时显示登录弹窗
          if (!this.isLoggedIn) {
            this.showLoginPrompt();
            return 'Home';
          }
          return 'UserProfile';
        case 'parental-control':
          return 'ParentalControl';
        case 'dispute-resolution':
          return 'DisputeResolution';
        case 'minor-protection':
          return 'MinorProtection';
        case 'about-us':
          return 'AboutUs';
        case 'contact-us':
          return 'ContactUs';
        case 'report-center':
          return 'ReportCenter';
        case 'home':
        default:
          return 'Home';
      }
    }
  },
  created() {
    // 检查是否已登录
    const token = localStorage.getItem('token');
    if (token) {
      this.isLoggedIn = true;
    }
  },
  mounted() {
    // 监听浮动播放器事件
    window.addEventListener('openAudioPlayer', this.handleOpenAudioPlayer);
    window.addEventListener('showFloatingPlayer', this.handleShowFloatingPlayer);
  },
  beforeUnmount() {
    window.removeEventListener('openAudioPlayer', this.handleOpenAudioPlayer);
    window.removeEventListener('showFloatingPlayer', this.handleShowFloatingPlayer);
  },
  methods: {
    handleLoginSuccess(userData) {
      this.isLoggedIn = true;
      this.showLoginModal = false;
      
      // 刷新MainLayout中的用户信息
      if (this.$refs.mainLayout) {
        // 如果有用户数据，立即更新
        if (userData) {
          this.$refs.mainLayout.userInfo = userData;
          localStorage.setItem('userInfo', JSON.stringify(userData));
        }
        // 再从服务器获取最新数据
        this.$refs.mainLayout.fetchUserInfo();
      }
      
      // 如果有待播放的视频，播放它
      if (this.pendingVideoId) {
        this.videoId = this.pendingVideoId;
        this.pendingVideoId = null;
      }
      
      // 如果有待播放的音频，播放它
      if (this.pendingAudioId) {
        this.audioId = this.pendingAudioId;
        this.pendingAudioId = null;
      }
    },
    handleLogout() {
      this.isLoggedIn = false;
      // 如果在播放视频或音频，返回首页
      if (this.videoId || this.audioId) {
        this.videoId = null;
        this.audioId = null;
        this.currentMenu = 'home';
      }
    },
    handleMenuChange(menu, categoryId) {
      this.currentMenu = menu;
      this.currentCategoryId = categoryId;
      // 清除视频和音频ID，返回到普通页面
      this.videoId = null;
      this.audioId = null;
    },
    handleNavigate(type) {
      // 处理Home组件发出的导航事件
      this.currentMenu = type;
      
      // 设置分类ID
      let categoryId = null;
      switch(type) {
        case 'mv':
          categoryId = 80;
          break;
        case 'singer':
          categoryId = 53;
          break;
        case 'all':
          categoryId = 100;
          break;
      }
      this.currentCategoryId = categoryId;
      
      // 清除视频和音频ID，返回到普通页面
      this.videoId = null;
      this.audioId = null;
    },
    showUserProfile() {
      // 检查是否登录
      if (!this.isLoggedIn) {
        this.showLoginPrompt();
        return;
      }
      
      this.currentMenu = 'profile';
      // 清除视频和音频ID，返回到普通页面
      this.videoId = null;
      this.audioId = null;
    },
    handlePlayVideo(id) {
      // 检查是否登录
      if (!this.isLoggedIn) {
        // 保存要播放的视频ID
        this.pendingVideoId = id;
        // 显示登录弹窗
        this.showLoginPrompt();
        return;
      }
      
      // 已登录，直接播放视频
      this.playVideo(id);
    },
    handlePlayAudio(id) {
      // 检查是否登录
      if (!this.isLoggedIn) {
        // 保存要播放的音频ID
        this.pendingAudioId = id;
        // 显示登录弹窗
        this.showLoginPrompt();
        return;
      }
      
      // 已登录，直接播放音频
      this.playAudio(id);
    },
    playVideo(id) {
      // 设置视频ID，显示视频播放页面
      this.videoId = id;  
      // 清除音频ID
      this.audioId = null;
    },
    playAudio(id) {
      // 设置音频ID，显示音频播放页面
      this.audioId = id;
      // 清除视频ID
      this.videoId = null;
    },
    closeVideo() {
      // 清除视频ID，返回到普通页面
      this.videoId = null;
    },
    closeAudio() {
      // 清除音频ID，返回到普通页面
      this.audioId = null;
    },
    updateVideoId(newId) {
      this.videoId = newId;
    },
    updateAudioId(newId) {
      this.audioId = newId;
    },
    showLoginPrompt() {
      this.showLoginModal = true;
    },
    cancelLogin() {
      this.showLoginModal = false;
      this.pendingVideoId = null;
      this.pendingAudioId = null;
    },
    handleOpenAudioPlayer(event) {
      // 从浮动播放器打开完整音频播放器
      const data = event.detail;
      this.audioId = data.audioId;
      
      // 如果有当前时间和播放索引，传递给AudioPlayer
      this.$nextTick(() => {
        // 这里可以通过ref或者事件传递数据给AudioPlayer
        // 由于使用了动态组件，需要等待组件渲染完成
      });
    },
    handleShowFloatingPlayer(event) {
      // 显示浮动播放器
      const data = event.detail;
      console.log('App received showFloatingPlayer event:', data);
      
      this.$nextTick(() => {
        if (this.$refs.mainLayout && this.$refs.mainLayout.$refs.floatingPlayer) {
          this.$refs.mainLayout.$refs.floatingPlayer.showPlayer(data);
        } else {
          console.error('FloatingPlayer ref not found');
        }
      });
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #333;
  background-color: #f5f5f5;
}

#app {
  height: 100vh;
}

/* 登录弹窗样式 */
.login-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.login-modal {
  background-color: #fff;
  border-radius: 8px;
  width: 420px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.login-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.login-modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.login-modal-body {
  padding: 20px;
}
</style>
